import { ASSISTANT_STORAGE_PREFIX } from '@/lib/session'

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

export const assistantStorageKey = (userId: string, companyId: string): string => `${ASSISTANT_STORAGE_PREFIX}:${userId}:${companyId}`

export interface AssistantPrefs {
  open: boolean
  conversationId: string | null
}

export const DEFAULT_ASSISTANT_PREFS: AssistantPrefs = { open: false, conversationId: null }

export function parseAssistantPrefs(raw: string | null | undefined): AssistantPrefs {
  if (!raw) return DEFAULT_ASSISTANT_PREFS
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!isRecord(parsed)) return DEFAULT_ASSISTANT_PREFS
    const conversationId = typeof parsed.conversationId === 'string' ? parsed.conversationId.trim() : null
    return { open: parsed.open === true, conversationId: conversationId?.startsWith('asc_') ? conversationId : null }
  } catch {
    return DEFAULT_ASSISTANT_PREFS
  }
}

export function readAssistantPrefs(key: string): AssistantPrefs {
  try {
    return parseAssistantPrefs(localStorage.getItem(key))
  } catch {
    return DEFAULT_ASSISTANT_PREFS
  }
}

export function writeAssistantPrefs(key: string, prefs: AssistantPrefs) {
  try {
    localStorage.setItem(key, JSON.stringify(prefs))
  } catch {
    return
  }
}

export const ASSISTANT_PANEL_WIDTH = { min: 360, max: 760, default: 420, step: 24 } as const

export const ASSISTANT_PANEL_WIDTH_KEY = `${ASSISTANT_STORAGE_PREFIX}-width`

export const assistantPanelMaxWidth = (viewport: number): number =>
  Math.max(ASSISTANT_PANEL_WIDTH.min, Math.min(ASSISTANT_PANEL_WIDTH.max, Math.floor(viewport * 0.5)))

export const clampAssistantPanelWidth = (width: number, viewport: number): number =>
  Math.round(Math.min(Math.max(width, ASSISTANT_PANEL_WIDTH.min), assistantPanelMaxWidth(viewport)))

export function parseAssistantPanelWidth(raw: string | null | undefined): number | null {
  if (!raw) return null
  const width = Number(raw)
  return Number.isFinite(width) && width > 0 ? Math.round(width) : null
}

export function readAssistantPanelWidth(): number | null {
  try {
    return parseAssistantPanelWidth(localStorage.getItem(ASSISTANT_PANEL_WIDTH_KEY))
  } catch {
    return null
  }
}

export function writeAssistantPanelWidth(width: number | null) {
  try {
    if (width == null) localStorage.removeItem(ASSISTANT_PANEL_WIDTH_KEY)
    else localStorage.setItem(ASSISTANT_PANEL_WIDTH_KEY, String(Math.round(width)))
  } catch {
    return
  }
}

export function assistantShortcutLabel(userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): string {
  return /Mac|iPhone|iPad/i.test(userAgent) ? '⌘ J' : 'Ctrl J'
}

export function looksLikeAssistantQuestion(query: string): boolean {
  const value = query.trim()
  if (!value) return false
  return value.endsWith('?') || value.split(/\s+/).length >= 3
}

export const assistantAskFirst = (query: string, hasHits: boolean): boolean => !!query.trim() && (!hasHits || looksLikeAssistantQuestion(query))
